import React from 'react';
import { replaceEmojis } from './emoji.util';
import { ChannelLink } from '@/components/mentions/ChannelLink';

/**
 * URL regex matching:
 * 1. http:// or https:// URLs
 * 2. www. prefixed URLs
 * 3. Bare domains with known TLDs (com, net, org, bg, io, ai)
 */
const BARE_TLDS = [
  // Generic
  'com', 'net', 'org', 'info', 'biz', 'co',
  // Tech / Dev
  'io', 'ai', 'dev', 'app', 'tech', 'me', 'cc',
  // Web design / Digital marketing
  'design', 'agency', 'studio', 'digital', 'media', 'online', 'site', 'web',
  // Business / Commerce
  'shop', 'store', 'space', 'fun',
  // EU member states + candidate countries
  'eu',
  'at', 'be', 'bg', 'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr',
  'de', 'gr', 'hu', 'ie', 'it', 'lv', 'lt', 'lu', 'mt', 'nl',
  'pl', 'pt', 'ro', 'sk', 'si', 'es', 'se',
  // UK, Turkey, other European
  'uk', 'tr', 'no', 'ch', 'is', 'rs', 'ba', 'me', 'mk', 'al', 'md', 'ua',
  // Other high-traffic
  'us', 'ca', 'au', 'nz', 'br', 'ru',
].join('|');
const URL_REGEX = new RegExp(
  `(https?:\\/\\/[^\\s<]+[^<.,:;"')\\]\\s]|www\\.[^\\s<]+[^<.,:;"')\\]\\s]|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.(?:${BARE_TLDS})(?:\\/[^\\s<]*[^<.,:;"')\\]\\s])?)`,
  'gi'
);

/**
 * Matches <mention data-user-id="uuid">@DisplayName</mention> and
 * <channel data-channel-id="uuid">#ChannelName</channel> in a single pass
 * so text segments between the tags interleave correctly.
 * Groups: 1 = mention userId, 2 = @DisplayName, 3 = channelId, 4 = #ChannelName
 */
const MENTION_OR_CHANNEL_REGEX =
  /<mention data-user-id="([a-f0-9-]{36})">(@[^<]+)<\/mention>|<channel data-channel-id="([a-f0-9-]{36})">(#[^<]+)<\/channel>/gi;

/** Matches special mentions: @everyone, @here, @channel, @всички */
const SPECIAL_MENTION_REGEX = /@(everyone|here|channel|всички)\b/gi;

/**
 * Strip mention and channel HTML tags, leaving only the display name (e.g. "@Асистент", "#счетоводство").
 * Useful for preview contexts (reply previews, DM sidebar, search results) that render plain text.
 */
export function stripMentionHtml(text: string): string {
  if (!text) return text;
  text = replaceEmojis(text);
  return text
    .replace(/<mention data-user-id="[a-f0-9-]{36}">(@[^<]+)<\/mention>/gi, '$1')
    .replace(/<channel data-channel-id="[a-f0-9-]{36}">(#[^<]+)<\/channel>/gi, '$1');
}

/**
 * Serialize tracked "#ChannelName" occurrences into <channel data-channel-id="uuid">#ChannelName</channel> tags.
 * Single-pass regex, longest name first, so one channel name being a prefix of
 * another can't match inside an already-inserted tag.
 */
export function serializeChannelReferences(text: string, channelMap: Map<string, string>): string {
  if (!text || channelMap.size === 0) return text;

  const escaped = [...channelMap.keys()]
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  return text.replace(
    new RegExp(escaped.join('|'), 'g'),
    (matched) => `<channel data-channel-id="${channelMap.get(matched)}">${matched}</channel>`
  );
}

/**
 * Process a plain text string for special mentions (@everyone, @here, @всички).
 * Returns an array of React nodes with special mentions rendered as styled badges.
 */
function processSpecialMentions(text: string, keyPrefix: string): React.ReactNode[] {
  if (!text) return [];

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  SPECIAL_MENTION_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = SPECIAL_MENTION_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(
      <span
        key={`${keyPrefix}-special-${match.index}`}
        className="inline-flex items-center px-1 py-0.5 rounded bg-amber-500/25 text-amber-300 font-semibold text-[13px] cursor-default"
      >
        @{match[1]}
      </span>
    );

    lastIndex = SPECIAL_MENTION_REGEX.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Linkify a plain text segment (no mention tags) — extracts URLs and special mentions.
 */
function linkifySegment(text: string, keyPrefix: string): React.ReactNode[] {
  if (!text) return [];

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  URL_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      // Process text before URL for special mentions
      parts.push(...processSpecialMentions(text.slice(lastIndex, match.index), `${keyPrefix}-pre-${match.index}`));
    }

    const matchedUrl = match[0];
    const href = /^https?:\/\//i.test(matchedUrl)
      ? matchedUrl
      : 'https://' + matchedUrl;

    parts.push(
      <a
        key={`${keyPrefix}-link-${match.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {matchedUrl}
      </a>
    );

    lastIndex = URL_REGEX.lastIndex;
  }

  if (lastIndex < text.length) {
    // Process remaining text for special mentions
    parts.push(...processSpecialMentions(text.slice(lastIndex), `${keyPrefix}-post`));
  }

  return parts;
}

/**
 * Parses message text — renders @mentions as styled spans, #channel references
 * as clickable links, and URLs as clickable links.
 * Returns an array of React nodes (strings and JSX elements).
 */
export function linkifyText(text: string): React.ReactNode[] {
  if (!text) return [text];

  // Convert emoticons and shortcodes to Unicode emoji before parsing
  text = replaceEmojis(text);

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  MENTION_OR_CHANNEL_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = MENTION_OR_CHANNEL_REGEX.exec(text)) !== null) {
    // Process text before this tag (may contain URLs)
    if (match.index > lastIndex) {
      parts.push(...linkifySegment(text.slice(lastIndex, match.index), `pre-${match.index}`));
    }

    if (match[1]) {
      const displayName = match[2]; // e.g. "@Асистент"

      parts.push(
        <span
          key={`mention-${match.index}`}
          className="inline-flex items-center px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium text-[13px] cursor-default"
        >
          {displayName}
        </span>
      );
    } else {
      parts.push(
        <ChannelLink
          key={`channel-${match.index}`}
          channelId={match[3]}
          label={match[4]} // e.g. "#счетоводство"
        />
      );
    }

    lastIndex = MENTION_OR_CHANNEL_REGEX.lastIndex;
  }

  // Process remaining text after last tag (may contain URLs)
  if (lastIndex < text.length) {
    parts.push(...linkifySegment(text.slice(lastIndex), 'post'));
  }

  return parts.length > 0 ? parts : [text];
}
